import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useCalendarData } from '../hooks/useCalendarData';
import { useCalendarViews } from '../hooks/useCalendarViews';
import { useHolidayFilters } from '../hooks/useHolidayFilters';
import type { Feriado, Demanda } from '../../../models';
import type { CalendarEvent } from '../hooks/useCalendarData';
import { ROUTES } from '../../../routes/constants';

export const useCalendarLogic = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedFeriado, setSelectedFeriado] = useState<Feriado | null>(null);
  const [isHolidayModalOpen, setIsHolidayModalOpen] = useState(false);
  const [showDemands, setShowDemands] = useState(true); // Toggle para mostrar/ocultar demandas

  // Check if user is admin
  const isAdmin = user?.funcao_usuario === 'ADMIN';
  
  // Check if user is gestor (manager) of any sector
  // NORMALIZAÇÃO: Converte 0/1 para false/true usando Boolean()
  const isGestor = user?.setores?.some(
    setor => setor.pivot?.funcao === 'GESTOR' && Boolean(setor.pivot?.active)
  ) || false;
  
  // Can manage holidays (create/edit/delete)
  const canManageHolidays = isAdmin || isGestor;

  // Hooks
  const {
    feriados,
    demandas,
    events,
    isLoading,
    error,
    refetch,
    getEventsForDate,
    getHolidaysForDate
  } = useCalendarData();

  const {
    view,
    currentDate,
    currentRange,
    changeView,
    navigateNext,
    navigatePrev,
    navigateToday
  } = useCalendarViews('month');

  const {
    filteredHolidays,
    holidaysByType,
    counts
  } = useHolidayFilters(feriados, currentRange);

  // Handle event click
  const handleEventClick = useCallback((event: CalendarEvent) => {
    if (event.type === 'holiday') {
      const feriado = event.data as Feriado;
      // Apenas gestores e admins podem editar feriados
      if (canManageHolidays) {
        setSelectedFeriado(feriado);
        setIsHolidayModalOpen(true);
      }
    } else if (event.type === 'demand') {
      const demanda = event.data as Demanda;
      if (demanda.id) {
        navigate(ROUTES.DEMANDS.VIEW(String(demanda.id), { type: 'all' }));
      }
    }
  }, [canManageHolidays, navigate]);

  // Handle holiday click from sidebar
  const handleHolidayClick = useCallback((feriado: Feriado) => {
    // Apenas gestores e admins podem editar feriados
    if (canManageHolidays) {
      setSelectedFeriado(feriado);
      setIsHolidayModalOpen(true);
    }
  }, [canManageHolidays]);

  // Handle holiday edit
  const handleEditHoliday = useCallback((feriado: Feriado) => {
    setSelectedFeriado(feriado);
    setIsHolidayModalOpen(true);
  }, []);

  // Handle add holiday
  const handleAddHoliday = useCallback(() => {
    setSelectedFeriado(null);
    setIsHolidayModalOpen(true);
  }, []);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    setIsHolidayModalOpen(false);
    setSelectedFeriado(null);
  }, []);

  // Handle modal success
  const handleModalSuccess = useCallback(() => {
    refetch(currentDate.getFullYear());
  }, [refetch, currentDate]);

  // Handle "Ver mais" - navigate to day view
  const handleShowMore = useCallback((date: Date) => {
    changeView('day', date);
  }, [changeView]);

  // Toggle mostrar/ocultar demandas
  const toggleShowDemands = useCallback(() => {
    setShowDemands(prev => !prev);
  }, []);

  return {
    // Data
    feriados,
    demandas,
    events,
    filteredHolidays,
    holidaysByType,
    counts,
    
    // State
    isLoading,
    error,
    isAdmin,
    isGestor,
    canManageHolidays,
    showDemands,
    
    // View
    view,
    currentDate,
    currentRange,
    
    // Modal
    isHolidayModalOpen,
    selectedFeriado,
    
    // Actions
    changeView,
    navigateNext,
    navigatePrev,
    navigateToday,
    handleEventClick,
    handleHolidayClick,
    handleEditHoliday,
    handleAddHoliday,
    handleModalClose,
    handleModalSuccess,
    handleShowMore,
    toggleShowDemands,
    getEventsForDate,
    getHolidaysForDate,
    refetch
  };
};

